import React, {useState, useRef, useCallback} from "react";
import "./RateInput.css"; // Pastikan Anda membuat file CSS ini

const Star = ({fillPercentage, onClick, onMouseEnter, onMouseLeave}) => {
	const uniqueId = React.useId(); // Untuk id clipPath yang unik
	const clipPathId = `starClip-${uniqueId}`;

	return (
		<svg
			className="star-svg"
			style={{cursor: "pointer"}}
			viewBox="0 0 24 24"
			onClick={onClick}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}>
			<defs>
				<clipPath id={clipPathId}>
					<rect height="100%" width={`${fillPercentage}%`} x="0" y="0" />
				</clipPath>
			</defs>

			{/* Background (outline) star - always gray */}
			<path
				d="M12 .587l3.668 7.568 8.332 1.207-6.001 5.855 1.416 8.286-7.415-3.906-7.417 3.906 1.416-8.286-6.001-5.855 8.332-1.207z"
				fill="#ccc" // Abu-abu
			/>

			{/* Filled star - clipped by fillPercentage */}
			<path
				clipPath={`url(#${clipPathId})`}
				d="M12 .587l3.668 7.568 8.332 1.207-6.001 5.855 1.416 8.286-7.415-3.906-7.417 3.906 1.416-8.286-6.001-5.855 8.332-1.207z"
				fill="#ff9800" // Oranye
			/>
		</svg>
	);
};

const RateInput = ({initialRating = 0, maxRating = 5, onChange = () => {}, disabled = false}) => {
	const [rating, setRating] = useState(initialRating);
	const [hoverRating, setHoverRating] = useState(0); // For preview on hover
	const sliderRef = useRef(null); // Reference for the slider bar
	const [isDragging, setIsDragging] = useState(false);

	// Helper function to calculate star fill percentage
	const getFillPercentage = (starIndex, currentRating) => {
		const starValue = starIndex + 1;

		if (currentRating >= starValue) {
			return 100; // Full star
		} else if (currentRating > starIndex && currentRating < starValue) {
			return (currentRating - starIndex) * 100; // Partial star
		}

		return 0; // Empty star
	};

	// Handles mouse enter on stars to preview 0.5 increments
	const handleStarMouseEnter = (starIndex, event) => {
		if (disabled) return; // Ignore hover if disabled
		const starRect = event.currentTarget.getBoundingClientRect();
		const x = event.clientX - starRect.left;
		const width = starRect.width;

		let calculatedRating;

		if (x <= width / 2) {
			calculatedRating = starIndex + 0.5;
		} else {
			calculatedRating = starIndex + 1;
		}
		setHoverRating(calculatedRating);
	};

	const handleStarMouseLeave = () => {
		setHoverRating(0);
	};

	const handleStarClick = (starIndex, event) => {
		if (disabled) return; // Ignore clicks if disabled
		const starRect = event.currentTarget.getBoundingClientRect();
		const x = event.clientX - starRect.left;
		const width = starRect.width;

		let clickedRating;

		if (x <= width / 2) {
			clickedRating = starIndex + 0.5;
		} else {
			clickedRating = starIndex + 1;
		}
		setRating(clickedRating);
		onChange(clickedRating);
		setHoverRating(0); // Clear hover state after click
	};

	// Slider interaction
	const calculateRatingFromMouseEvent = useCallback(
		event => {
			if (!sliderRef.current) return initialRating;

			const rect = sliderRef.current.getBoundingClientRect();
			const x = event.clientX - rect.left; // x position inside the slider
			let newRating = (x / rect.width) * maxRating;

			// Round to nearest 0.5 increment
			newRating = Math.round(newRating * 2) / 2;

			// Clamp between 0 and maxRating
			return Math.max(0, Math.min(maxRating, newRating));
		},
		[maxRating, initialRating],
	);

	const handleSliderClick = event => {
		if (disabled) return; // Ignore clicks if disabled
		const newRating = calculateRatingFromMouseEvent(event);

		setRating(newRating);
		onChange(newRating);
		setHoverRating(0);
	};

	const handleSliderMouseEnter = event => {
		if (disabled) return; // Ignore hover if disabled
		// Only show hover preview if not dragging
		if (!isDragging) {
			const newRating = calculateRatingFromMouseEvent(event);

			setHoverRating(newRating);
		}
	};

	const handleSliderMouseMove = event => {
		if (disabled) return; // Ignore mouse move if disabled
		if (isDragging) {
			const newRating = calculateRatingFromMouseEvent(event);

			setRating(newRating);
			onChange(newRating);
			setHoverRating(newRating); // Keep hover updated while dragging
		} else {
			// For general mouse movement on slider (preview)
			const newRating = calculateRatingFromMouseEvent(event);

			setHoverRating(newRating);
		}
	};

	const handleSliderMouseLeave = () => {
		if (disabled) return; // Ignore mouse leave if disabled
		if (!isDragging) {
			setHoverRating(0);
		}
	};

	const handleDotMouseDown = () => {
		setIsDragging(true);
	};

	const handleMouseUp = useCallback(() => {
		if (disabled) return; // Ignore mouse up if disabled
		if (isDragging) {
			setIsDragging(false);
			setHoverRating(0); // Clear hover after drag ends
		}
	}, [disabled, isDragging]);

	const handleMouseMove = useCallback(
		event => {
			if (isDragging) {
				const newRating = calculateRatingFromMouseEvent(event);

				setRating(newRating);
				setHoverRating(newRating);
			}
		},
		[isDragging, calculateRatingFromMouseEvent],
	);

	// Add global mouseup/mousemove listeners for dragging
	React.useEffect(() => {
		if (disabled) return;
		document.addEventListener("mouseup", handleMouseUp);
		document.addEventListener("mousemove", handleMouseMove);

		return () => {
			document.removeEventListener("mouseup", handleMouseUp);
			document.removeEventListener("mousemove", handleMouseMove);
		};
	}, [handleMouseUp, handleMouseMove, disabled]);

	// Determine which rating to use for display (hover takes precedence)
	const displayRating = hoverRating > 0 ? hoverRating : rating;

	// Calculate slider fill width and dot position
	const sliderFillWidth = (displayRating / maxRating) * 100;
	const dotPosition = (displayRating / maxRating) * 100;

	return (
		<div className="rating-input-wrapper">
			<label className="setting-label">Rating</label>
			<div className="rating-stars-container">
				{[...Array(maxRating)].map((_, index) => (
					<Star
						key={index}
						fillPercentage={getFillPercentage(index, displayRating)}
						onClick={e => handleStarClick(index, e)}
						onMouseEnter={e => handleStarMouseEnter(index, e)}
						onMouseLeave={handleStarMouseLeave}
					/>
				))}
				<div
					style={{
						padding: "8px",
						display: "flex",
						direction: "row",
						backgroundColor: "#f0f0f0",
						borderRadius: "4px",
					}}>
					<img alt="Star" src="/img/star.svg" style={{marginRight: "4px"}} />
					{displayRating.toFixed(1)}
				</div>
			</div>

			<div
				ref={sliderRef}
				className="rating-slider-bar"
				onClick={handleSliderClick}
				onMouseEnter={handleSliderMouseEnter}
				onMouseLeave={handleSliderMouseLeave}
				onMouseMove={handleSliderMouseMove}>
				<div className="rating-slider-fill" style={{width: `${sliderFillWidth}%`}} />

				<div className="rating-slider-dot" style={{left: `${dotPosition}%`}} onMouseDown={handleDotMouseDown} />

				{/* Vertical lines for 0.5 increments */}
				{[...Array(maxRating * 2)].map((_, index) => {
					const lineValue = (index + 1) * 0.5;
					const linePosition = (lineValue / maxRating) * 100;
					const isMajorLine = lineValue % 1 === 0; // Full number line

					// Skip line at 0%
					if (lineValue === 0) return null;

					return (
						<div
							key={`line-${index}`}
							className={`rating-segment-line ${isMajorLine ? "major" : ""}`}
							style={{left: `${linePosition}%`}}
						/>
					);
				})}
			</div>
			{/* <p>Selected Rating: {rating.toFixed(1)}</p> */}
		</div>
	);
};

export default RateInput;
